import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LuArrowRight,
  LuBell,
  LuBriefcase,
  LuBuilding2,
  LuCheck,
  LuChevronDown,
  LuCircleCheck,
  LuCloud,
  LuClock,
  LuCreditCard,
  LuEye,
  LuFileText,
  LuHeart,
  LuLandmark,
  LuLock,
  LuMail,
  LuPiggyBank,
  LuPlay,
  LuPlus,
  LuScrollText,
  LuShield,
  LuShieldCheck,
  LuTarget,
  LuTrendingUp,
  LuUpload,
  LuUserCheck,
  LuUsers,
  LuX,
} from 'react-icons/lu';
import './PublicSite.css';
import Seo, {
  buildBreadcrumbSchema,
  buildFaqSchema,
  organizationSchema,
  websiteSchema,
} from '../components/Seo';

const DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=pTjhxvFu7A8&t=27s';

const startAccount = () => {
  window.dispatchEvent(new Event('open-auth-modal'));
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const startAddNominee = () => {
  sessionStorage.setItem('openAddNominee', '1');
  window.dispatchEvent(new Event('open-auth-modal'));
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const useCounter = (target, duration = 1800) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
};

const heroPills = [
  { icon: LuLandmark, label: 'Bank & FD' },
  { icon: LuBriefcase, label: 'PPF & EPF' },
  { icon: LuTrendingUp, label: 'Stocks & mutual funds' },
  { icon: LuFileText, label: 'Insurance & LIC' },
  { icon: LuBuilding2, label: 'Property & loans' },
  { icon: LuScrollText, label: 'Will & documents' },
];

const assetHubRows = [
  [
    { icon: LuLandmark, label: 'Bank & FD' },
    { icon: LuPiggyBank, label: 'PPF' },
    { icon: LuBriefcase, label: 'EPF' },
  ],
  [
    { icon: LuFileText, label: 'Insurance' },
    null,
    { icon: LuTrendingUp, label: 'Stocks & MF' },
  ],
  [
    { icon: LuBuilding2, label: 'Property' },
    { icon: LuCreditCard, label: 'Loans' },
    { icon: LuScrollText, label: 'Will' },
  ],
];

const problemSteps = [
  'Father passes away',
  'Family doesn\u2019t know',
  'Insurance expires unclaimed',
  'Fixed deposit forgotten',
  'Bank locker unknown',
  'Money lost forever',
];

const trustBadges = [
  'Google Login',
  'AES-256 Encryption',
  'SSL Secured',
  'Hosted on Google Cloud',
  'Private by design',
  'No data sold',
  'Your data belongs to you',
];

const stats = [
  { value: 500, suffix: '+', label: 'Documents Protected' },
  { value: 50, suffix: '+', label: 'Families Protected' },
  { value: 99.9, suffix: '%', label: 'Uptime', decimal: true },
  { value: 0, suffix: '', label: 'Data Sold' },
];

const features = [
  { icon: LuLandmark, title: 'Never Lose Bank & Government Savings', types: ['Bank accounts', 'Fixed deposits', 'PPF', 'EPF'] },
  { icon: LuTrendingUp, title: 'Track Every Investment', types: ['Mutual funds', 'Stocks', 'LIC', 'Bonds'] },
  { icon: LuBuilding2, title: 'Protect Property & Loans', types: ['Property', 'Home loan', 'Vehicle', 'Passport'] },
  { icon: LuScrollText, title: 'Store Critical Documents', types: ['Will', 'Insurance', 'Locker', 'Nominee records'] },
];

const howSteps = [
  { num: '1', title: 'Create account', desc: 'Sign in securely with Google in under a minute.' },
  { num: '2', title: 'Upload documents', desc: 'Store insurance, investments, property records and more.' },
  { num: '3', title: 'Add nominees', desc: 'Name the people you trust to receive information when needed.' },
  { num: '4', title: 'Verify', desc: 'We verify family requests before anything is shared.' },
  { num: '5', title: 'Relax', desc: 'Your family will know where to begin when it matters most.' },
];

const comparisonRows = [
  ['Documents only', 'Documents + nominees + alerts'],
  ['Cloud storage', 'Smart family notifications'],
  ['Manual sharing', 'Automatic inheritance guidance'],
  ['No reminders', 'Family protection built in'],
  ['Generic storage', 'Designed for real family finances'],
];

const faqs = [
  {
    q: 'What happens if I die?',
    a: 'A trusted family member can start a verified request. After verification, your nominees receive the information they need\u2014not unrestricted access to your accounts.',
  },
  {
    q: 'Who can access my documents?',
    a: 'Only you while you are alive. Nominees receive limited, verified information only after a family request is approved\u2014never automatic or instant access.',
  },
  {
    q: 'Can KeepMyAsset read my files?',
    a: 'Your records are encrypted and stored securely. We do not sell your data. Our team cannot browse your documents like a social feed.',
  },
  {
    q: 'Can I remove everything?',
    a: 'Yes. You can update or delete your records and nominee details at any time. Contact support if you need help closing your account.',
  },
  {
    q: 'Can I trust this?',
    a: 'We use Google login, AES-256 encryption, SSL, and Google Cloud hosting. We are transparent about what we do\u2014and do not\u2014promise.',
  },
];

const extendedFaqs = [
  ...faqs,
  {
    q: 'Is KeepMyAsset free to use?',
    a: 'Yes. You can start free with up to 5 documents and 2 nominees. Add assets and nominees at your own pace—no credit card required.',
  },
  {
    q: 'What types of assets can I store?',
    a: 'Bank accounts, fixed deposits, PPF, EPF, mutual funds, stocks, LIC and insurance policies, property papers, home loans, vehicle documents, wills, passport copies, locker details, and more.',
  },
  {
    q: 'Does KeepMyAsset replace legal nomination?',
    a: 'No. KeepMyAsset helps your family find and understand what exists—it does not transfer ownership, process claims, or replace nominations you make with banks, insurers, or regulators.',
  },
  {
    q: 'How does Secure Mode work?',
    a: 'When Secure Mode is on, nominee checks require manual verification before any information is shared. This adds an extra layer of protection for sensitive family records.',
  },
  {
    q: 'Can nominees log in and see everything immediately?',
    a: 'Never. Nominees do not get automatic or instant access to your documents. Information is shared only after a verified family request is approved.',
  },
  {
    q: 'Where is my data stored?',
    a: 'Your encrypted records are hosted on secure Google Cloud infrastructure with SSL in transit and industry-standard encryption at rest.',
  },
  {
    q: 'How long does setup take?',
    a: 'Most families add their first document and nominee in under 2 minutes. You can build your complete protection profile over time—there is no rush to do everything at once.',
  },
];

const detailedHowSteps = [
  {
    num: '01',
    icon: LuUserCheck,
    title: 'Create your account',
    desc: 'Sign in with Google—no new password to remember. Your account is ready in under a minute.',
    detail: 'We use Google authentication so you never share passwords with us directly.',
  },
  {
    num: '02',
    icon: LuUpload,
    title: 'Upload your assets',
    desc: 'Add bank details, PPF, EPF, stocks, insurance, property papers, loans, wills, and more.',
    detail: 'Upload PDF, JPG, or PNG files up to 2MB. Organize everything your family might need to find.',
  },
  {
    num: '03',
    icon: LuUsers,
    title: 'Add nominees',
    desc: 'Name the people you trust—spouse, children, parents, or a close relative.',
    detail: 'Add email and phone so we can reach them when a verified family request is approved.',
  },
  {
    num: '04',
    icon: LuShieldCheck,
    title: 'We verify requests',
    desc: 'When needed, a family member starts a request. We verify identity before sharing anything.',
    detail: 'No automatic access. No instant downloads. Every share goes through a careful check.',
  },
  {
    num: '05',
    icon: LuBell,
    title: 'Family gets guided',
    desc: 'Verified nominees receive only the information they need to take the next steps.',
    detail: 'They learn what exists and where to begin—not unrestricted access to your accounts.',
  },
];

const verificationFlow = [
  {
    icon: LuUsers,
    title: 'A family member requests',
    desc: 'When something happens, a trusted relative initiates a nominee check through KeepMyAsset.',
  },
  {
    icon: LuShieldCheck,
    title: 'We verify carefully',
    desc: 'Our team validates the request. Secure Mode adds extra manual verification when enabled.',
  },
  {
    icon: LuBell,
    title: 'Nominees are informed',
    desc: 'Approved nominees receive guidance on policies, accounts, and documents—not login credentials.',
  },
];

const securityPillars = [
  {
    icon: LuLock,
    title: 'AES-256 encryption',
    desc: 'Documents encrypted at rest and in transit. The same standard banks and hospitals rely on.',
  },
  {
    icon: LuUserCheck,
    title: 'Verified access only',
    desc: 'Nominees never get instant access. Every share requires a verified family request first.',
  },
  {
    icon: LuCloud,
    title: 'Google Cloud hosting',
    desc: 'Redundant, enterprise-grade infrastructure with regular backups and data isolation.',
  },
  {
    icon: LuShield,
    title: 'No employee browsing',
    desc: 'Our team cannot casually browse your files. Your records are not a social feed.',
  },
  {
    icon: LuEye,
    title: 'Zero data selling',
    desc: 'We do not sell, rent, or trade your personal information. Your data belongs to you.',
  },
  {
    icon: LuCircleCheck,
    title: 'Google login security',
    desc: 'Sign in with Google—no passwords stored on our servers. You control your Google account.',
  },
];

const aboutValues = [
  {
    icon: LuHeart,
    title: 'Family first',
    desc: 'Every feature exists to help loved ones find what matters—not to sell storage.',
  },
  {
    icon: LuShield,
    title: 'Privacy by design',
    desc: 'Encryption, verified access, and transparency built in from day one.',
  },
  {
    icon: LuTarget,
    title: 'Built for your assets',
    desc: 'PPF, EPF, LIC, FD, mutual funds—the assets families actually hold.',
  },
  {
    icon: LuEye,
    title: 'Honest promises',
    desc: 'We tell you what we do and what we never will—no legal claims we cannot keep.',
  },
];

const aboutNotList = [
  'We are not a bank, broker, or insurer',
  'We do not transfer money or process claims',
  'We do not replace legal nominations',
  'We do not give nominees unrestricted account access',
];

function SubPageHero({ eyebrow, title, lead, icon: Icon, variant = 'default', children }) {
  return (
    <section className={`sub-hero sub-hero-${variant}`}>
      {Icon && (
        <div className="sub-hero-icon" aria-hidden="true">
          <Icon />
        </div>
      )}
      <div className="sub-hero-copy">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="lead">{lead}</p>
        {children}
      </div>
    </section>
  );
}

function SubPageCTA({ title, desc, buttonLabel = 'Start Organizing Assets' }) {
  return (
    <>
      <section className="sub-cta">
        <div>
          <p className="eyebrow">GET STARTED</p>
          <h2>{title}</h2>
          <p>{desc}</p>
        </div>
        <button type="button" className="button button-accent" onClick={startAccount}>
          {buttonLabel} <LuArrowRight aria-hidden="true" />
        </button>
      </section>
      <div className="mobile-sticky-cta">
        <button type="button" className="button button-accent" onClick={startAccount}>
          {buttonLabel}
        </button>
      </div>
    </>
  );
}

function HowItWorksPage() {
  return (
    <>
      <Seo
        title="How It Works"
        description="Learn how KeepMyAsset works in 5 simple steps: create your account, upload assets, add nominees, verify family requests, and give your family peace of mind."
        path="/how-it-works"
        keywords="how keepmyasset works, nominee alert process, asset protection steps, family asset storage"
        jsonLd={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'How It Works', path: '/how-it-works' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to protect your family assets with KeepMyAsset',
            description:
              'Five steps to record assets, assign nominees, and ensure your family is guided when it matters most.',
            step: detailedHowSteps.map((step, index) => ({
              '@type': 'HowToStep',
              position: index + 1,
              name: step.title,
              text: step.desc,
            })),
          },
        ]}
      />
    <div className="public-site public-page">
      <SubPageHero
        eyebrow="HOW IT WORKS"
        title="Five steps to lifelong peace of mind"
        lead="KeepMyAsset records your assets, maps your nominees, and guides your family through a verified process—never automatic or unrestricted sharing."
        icon={LuClock}
        variant="mint"
      />

      <section className="sub-section">
        <div className="sub-timeline">
          {detailedHowSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <article className="sub-timeline-step" key={step.num}>
                <div className="sub-timeline-marker">
                  <span>{step.num}</span>
                </div>
                <div className="sub-timeline-body">
                  <div className="sub-timeline-head">
                    <Icon aria-hidden="true" />
                    <h2>{step.title}</h2>
                  </div>
                  <p>{step.desc}</p>
                  <p className="sub-timeline-detail">{step.detail}</p>
                </div>
                {index < detailedHowSteps.length - 1 && (
                  <div className="sub-timeline-line" aria-hidden="true" />
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="sub-section sub-section-soft">
        <div className="section-intro center">
          <p className="eyebrow">WHAT YOU CAN STORE</p>
          <h2>One place for every asset your family should know about.</h2>
        </div>
        <div className="sub-chip-grid">
          {['Bank & FD', 'PPF', 'EPF', 'Stocks', 'Mutual funds', 'LIC', 'Insurance', 'Property', 'Home loan', 'Vehicle', 'Will', 'Locker'].map(
            (item) => (
              <span className="sub-chip" key={item}>
                <LuCheck aria-hidden="true" /> {item}
              </span>
            )
          )}
        </div>
      </section>

      <section className="sub-section">
        <div className="section-intro center">
          <p className="eyebrow">VERIFICATION FLOW</p>
          <h2>Why families trust the process</h2>
          <p>No nominee gets instant access. Ever.</p>
        </div>
        <div className="sub-card-grid three">
          {verificationFlow.map(({ icon: Icon, title, desc }) => (
            <article className="sub-card" key={title}>
              <Icon className="sub-card-icon" aria-hidden="true" />
              <h3>{title}</h3>
              <p>{desc}</p>
            </article>
          ))}
        </div>
      </section>

      <SubPageCTA
        title="Ready to protect your family?"
        desc="Start free in under 2 minutes. Add your first asset and nominee today."
        buttonLabel="Start Free"
      />
    </div>
    </>
  );
}

function SecurityPage() {
  return (
    <>
      <Seo
        title="Security & Privacy"
        description="KeepMyAsset uses AES-256 encryption, Google Cloud hosting, verified access controls, and a strict no-data-selling policy to protect your family's financial records."
        path="/security"
        keywords="keepmyasset security, asset encryption, nominee data privacy, AES-256, Google Cloud"
        jsonLd={buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Security', path: '/security' },
        ])}
      />
    <div className="public-site public-page">
      <SubPageHero
        eyebrow="SECURITY & PRIVACY"
        title="Your family's data deserves bank-grade protection"
        lead="Encryption, verified access, Google Cloud hosting, and a strict no-sell policy—not marketing fluff, but how we actually built KeepMyAsset."
        icon={LuLock}
        variant="dark"
      >
        <div className="sub-hero-badges">
          {trustBadges.slice(0, 5).map((badge) => (
            <span className="trust-badge" key={badge}>
              <LuCheck aria-hidden="true" /> {badge}
            </span>
          ))}
        </div>
      </SubPageHero>

      <section className="sub-section">
        <div className="stats-grid">
          {stats.map((stat) => (
            <StatCard key={stat.label} stat={stat} />
          ))}
        </div>
      </section>

      <section className="sub-section">
        <div className="section-intro center">
          <p className="eyebrow">SECURITY PILLARS</p>
          <h2>How we protect what matters most</h2>
        </div>
        <div className="sub-card-grid">
          {securityPillars.map(({ icon: Icon, title, desc }) => (
            <article className="sub-card sub-card-dark" key={title}>
              <Icon className="sub-card-icon" aria-hidden="true" />
              <h3>{title}</h3>
              <p>{desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="sub-section sub-section-dark">
        <div className="sub-split">
          <div>
            <p className="eyebrow light">OUR PROMISE</p>
            <h2>We will never sell your data. Period.</h2>
            <p>
              Your insurance policies, bank details, and nominee information exist for one reason: so
              your family can find them when needed. Not for ads. Not for brokers. Not for anyone else.
            </p>
          </div>
          <ul className="sub-promise-list">
            {['AES-256 encryption at rest & in transit', 'SSL on every connection', 'Data isolated per user', 'Regular encrypted backups', 'No third-party data sharing'].map(
              (item) => (
                <li key={item}>
                  <LuCircleCheck aria-hidden="true" /> {item}
                </li>
              )
            )}
          </ul>
        </div>
      </section>

      <section className="sub-section sub-section-soft">
        <div className="section-intro center">
          <p className="eyebrow">ACCESS CONTROL</p>
          <h2>You control who sees what—and when</h2>
        </div>
        <div className="sub-card-grid three">
          <article className="sub-card">
            <LuUserCheck className="sub-card-icon" aria-hidden="true" />
            <h3>While you&apos;re here</h3>
            <p>Only you can view, upload, edit, or delete your records. Full control stays with you.</p>
          </article>
          <article className="sub-card">
            <LuShieldCheck className="sub-card-icon" aria-hidden="true" />
            <h3>When family requests</h3>
            <p>A verified relative initiates a check. We validate before any information is shared.</p>
          </article>
          <article className="sub-card">
            <LuBell className="sub-card-icon" aria-hidden="true" />
            <h3>What nominees receive</h3>
            <p>Guidance and document access they need to act—not passwords or unrestricted logins.</p>
          </article>
        </div>
      </section>

      <SubPageCTA
        title="Store with confidence. Protect with purpose."
        desc="Join families who trust KeepMyAsset with their most sensitive records."
      />
    </div>
    </>
  );
}

function FaqPage() {
  return (
    <>
      <Seo
        title="FAQ"
        description="Answers to common questions about KeepMyAsset: nominee alerts, asset storage, security, pricing, Secure Mode, and what happens to your family's records."
        path="/faq"
        keywords="keepmyasset faq, nominee alert questions, asset protection help, family finance security"
        jsonLd={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'FAQ', path: '/faq' },
          ]),
          buildFaqSchema(extendedFaqs),
        ]}
      />
    <div className="public-site public-page">
      <SubPageHero
        eyebrow="FREQUENTLY ASKED QUESTIONS"
        title="Straight answers before you trust us with your family's records"
        lead="KeepMyAsset helps families find assets and guide nominees—it does not transfer money, replace legal nominations, or give instant access to anyone."
        icon={LuCircleCheck}
        variant="default"
      />

      <section className="sub-section">
        <div className="faq-list faq-list-page">
          {extendedFaqs.map(({ q, a }) => (
            <details className="faq-item" key={q}>
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="sub-section sub-section-soft">
        <div className="sub-contact-card">
          <LuMail className="sub-contact-icon" aria-hidden="true" />
          <div>
            <h3>Still have questions?</h3>
            <p>
              Email us at{' '}
              <a href="mailto:team.keepmyasset@gmail.com">team.keepmyasset@gmail.com</a> or{' '}
              <a href="https://wa.me/918318959079" target="_blank" rel="noopener noreferrer">
                WhatsApp us
              </a>
              . We typically respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      <SubPageCTA
        title="Convinced? Start protecting your family today."
        desc="Free to begin. No credit card. Google login in under a minute."
        buttonLabel="Start Free"
      />
    </div>
    </>
  );
}

function AboutPage() {
  return (
    <>
      <Seo
        title="About Us"
        description="KeepMyAsset was built to stop ₹82,000 crore+ from going unclaimed. Learn our mission to help Indian families protect assets and inform nominees when it matters most."
        path="/about"
        keywords="about keepmyasset, nominee alert company, family asset protection India, unclaimed money"
        jsonLd={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'About', path: '/about' },
          ]),
          organizationSchema,
        ]}
      />
    <div className="public-site public-page">
      <SubPageHero
        eyebrow="ABOUT KEEPMYASSET"
        title="Built because families deserve to know"
        lead="₹82,000 crore lies unclaimed—not because families don't care, but because they were never told about the assets that existed."
        icon={LuHeart}
        variant="warm"
      />

      <section className="sub-section">
        <div className="sub-stat-banner">
          <strong>₹82,000 Cr+</strong>
          <span>unclaimed because nominees were never informed</span>
        </div>
        <div className="sub-story">
          <p>
            When someone passes away, families often discover—too late—that insurance policies expired
            unclaimed, fixed deposits were forgotten, EPF accounts went untouched, and property documents
            were locked in drawers no one knew about.
          </p>
          <p>
            KeepMyAsset was built to prevent that silence. We give you one secure place to record every
            asset, assign nominees, and ensure your family is guided when it matters most.
          </p>
        </div>
      </section>

      <section className="sub-section sub-section-soft">
        <div className="section-intro center">
          <p className="eyebrow">OUR VALUES</p>
          <h2>What drives everything we build</h2>
        </div>
        <div className="sub-card-grid">
          {aboutValues.map(({ icon: Icon, title, desc }) => (
            <article className="sub-card" key={title}>
              <Icon className="sub-card-icon" aria-hidden="true" />
              <h3>{title}</h3>
              <p>{desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="sub-section">
        <div className="sub-dual-cards">
          <article className="sub-card sub-card-positive">
            <p className="eyebrow">WHAT WE ARE</p>
            <h3>A family asset protection platform</h3>
            <ul>
              <li><LuCheck aria-hidden="true" /> Secure record-keeping for all your assets</li>
              <li><LuCheck aria-hidden="true" /> Nominee mapping with verified notifications</li>
              <li><LuCheck aria-hidden="true" /> Guidance for families during difficult times</li>
              <li><LuCheck aria-hidden="true" /> Designed for everyday financial life</li>
            </ul>
          </article>
          <article className="sub-card sub-card-muted">
            <p className="eyebrow">WHAT WE ARE NOT</p>
            <h3>Clear boundaries, honest promises</h3>
            <ul>
              {aboutNotList.map((item) => (
                <li key={item}>
                  <LuX aria-hidden="true" /> {item}
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="sub-section sub-section-soft">
        <div className="sub-contact-card">
          <LuHeart className="sub-contact-icon" aria-hidden="true" />
          <div>
            <h3>Talk to us</h3>
            <p>
              KeepMyAsset helps families organize assets and guide nominees when it matters most. Reach out anytime at{' '}
              <a href="mailto:team.keepmyasset@gmail.com">team.keepmyasset@gmail.com</a> or on{' '}
              <a href="https://wa.me/918318959079" target="_blank" rel="noopener noreferrer">
                WhatsApp
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <SubPageCTA
        title="Your family deserves to know."
        desc="Start free today. Add your first asset in minutes."
        buttonLabel="Start Organizing Assets"
      />
    </div>
    </>
  );
}

function StatCard({ stat }) {
  const count = useCounter(stat.decimal ? stat.value * 10 : stat.value);
  const display = stat.decimal ? (count / 10).toFixed(1) : count.toLocaleString();
  return (
    <div className="stat-card">
      <div className="stat-value">
        {display}
        {stat.suffix}
      </div>
      <div className="stat-label">{stat.label}</div>
    </div>
  );
}

function DashboardMock() {
  return (
    <div className="dashboard-mock" aria-hidden="true">
      <div className="mock-toolbar">
        <span className="mock-dot red" />
        <span className="mock-dot yellow" />
        <span className="mock-dot green" />
        <span className="mock-title">KeepMyAsset Dashboard</span>
      </div>
      <div className="mock-body">
        <div className="mock-sidebar">
          <div className="mock-nav-item active">Assets</div>
          <div className="mock-nav-item">Nominees</div>
          <div className="mock-nav-item">Documents</div>
          <div className="mock-nav-item">Notifications</div>
          <div className="mock-nav-item">Security</div>
        </div>
        <div className="mock-main">
          <div className="mock-score">
            <span>Protection Score</span>
            <strong>78%</strong>
          </div>
          <div className="mock-alerts">
            <div className="mock-alert warn">Missing nominee on 1 policy</div>
            <div className="mock-alert info">2 document types not uploaded yet</div>
          </div>
          <div className="mock-cards">
            <div className="mock-card"><LuUpload /> Upload document</div>
            <div className="mock-card"><LuUsers /> 3 Nominees</div>
            <div className="mock-card"><LuBell /> Notifications on</div>
            <div className="mock-card"><LuLock /> AES-256</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssetHeroVisual() {
  return (
    <div className="asset-hero-visual" aria-hidden="true">
      <div className="asset-hub">
        {assetHubRows.map((row, rowIndex) =>
          row.map((item, colIndex) => {
            if (item === null) {
              return (
                <div className="asset-hub-center" key="center">
                  <LuShieldCheck aria-hidden="true" />
                  <span>All assets</span>
                </div>
              );
            }
            const Icon = item.icon;
            return (
              <div className="asset-tile" key={`${rowIndex}-${item.label}`}>
                <Icon aria-hidden="true" />
                <span>{item.label}</span>
              </div>
            );
          })
        )}
      </div>
      <p>Every asset your family should know about</p>
      <p className="asset-hero-sub">Built for families, not file cabinets</p>
    </div>
  );
}

export const PublicHome = () => {
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    const timer = window.setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, []);

  return (
  <>
    <Seo
      title="Nominee Alert & Asset Protection for Families"
      description="KeepMyAsset is one secure place for every asset your family should know about—bank accounts, PPF, EPF, stocks, insurance, property, and more. Assign nominees and protect your family."
      path="/"
      keywords="keepmyasset, nominee alert, nominee check, asset protection, family finance India, PPF nominee, EPF nominee, insurance nominee"
      jsonLd={[organizationSchema, websiteSchema]}
    />
  <div className="public-site">
    {/* Hero */}
    <section className="public-hero" id="top">
      <div className="public-hero-copy">
        <p className="eyebrow">
          <LuHeart aria-hidden="true" /> Peace of mind for your family
        </p>
        <h1>
          One secure place for every asset your family should know about
          <span className="hero-headline-sub">Even when you&apos;re no longer here</span>
        </h1>
        <p className="lead">
          Store bank accounts, PPF, EPF, stocks, insurance, property, loans, and more in one secure
          place—so nominees are informed when your family needs them most.
        </p>
        <div className="hero-pills">
          {heroPills.map(({ icon: Icon, label }) => (
            <span className="hero-pill" key={label}>
              <Icon aria-hidden="true" />
              {label}
            </span>
          ))}
        </div>
        <div className="public-actions">
          <button type="button" className="button button-accent" onClick={startAccount}>
            Start Free <LuArrowRight aria-hidden="true" />
          </button>
          <button type="button" className="button button-success" onClick={startAddNominee}>
            <LuPlus aria-hidden="true" /> Add New Nominee
          </button>
          <a
            href={DEMO_VIDEO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="button button-secondary"
          >
            <LuPlay aria-hidden="true" /> Watch Demo
          </a>
        </div>
        <p className="hero-note">
          <LuShieldCheck aria-hidden="true" /> Free to start · Google login · No credit card required
        </p>
      </div>
      <div className="hero-visual">
        <AssetHeroVisual />
      </div>
    </section>

    {/* Product screenshot + demo */}
    <section className="product-preview" id="demo">
      <p className="preview-label">See the product — no imagination required</p>
      <DashboardMock />
      <div className="demo-callout">
        <div className="demo-play-icon"><LuPlay aria-hidden="true" /></div>
        <div>
          <strong>2-minute walkthrough</strong>
          <p>Upload a document, add a nominee, and see how your family gets protected.</p>
        </div>
        <button type="button" className="button button-secondary" onClick={() => scrollTo('how-it-works')}>
          See the steps
        </button>
      </div>
    </section>

    {/* Problem story */}
    <section className="public-section problem-section" id="story">
      <div className="section-intro center">
        <p className="eyebrow">THE PROBLEM</p>
        <h2>When families don&apos;t know, money disappears.</h2>
        <p>₹82,000 crore lies unclaimed because nominees were never told.</p>
      </div>
      <div className="problem-flow">
        {problemSteps.map((step, i) => (
          <React.Fragment key={step}>
            <div className={`problem-step ${i === problemSteps.length - 1 ? 'solution' : ''}`}>
              {step}
            </div>
            {i < problemSteps.length - 1 && (
              <LuChevronDown className="problem-arrow" aria-hidden="true" />
            )}
          </React.Fragment>
        ))}
        <div className="problem-solution">
          <LuShieldCheck aria-hidden="true" />
          <strong>KeepMyAsset prevents this</strong>
        </div>
      </div>
    </section>

    {/* Trust */}
    <section className="public-section trust-section" id="trust">
      <div className="trust-badges">
        {trustBadges.map((badge) => (
          <span className="trust-badge" key={badge}>
            <LuCheck aria-hidden="true" /> {badge}
          </span>
        ))}
      </div>
      <div className="stats-grid">
        {stats.map((stat) => (
          <StatCard key={stat.label} stat={stat} />
        ))}
      </div>
    </section>

    {/* Features */}
    <section className="public-section" id="features">
      <div className="section-intro">
        <p className="eyebrow">FEATURES</p>
        <h2>Everything your family needs to find what matters.</h2>
        <p>Not just storage—a complete protection system for the people you love.</p>
      </div>
      <div className="features-grid">
        {features.map(({ icon: Icon, title, types }) => (
          <article className="feature-card" key={title}>
            <Icon className="feature-icon" aria-hidden="true" />
            <h3>{title}</h3>
            <div className="feature-tags">
              {types.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>

    {/* How it works */}
    <section className="public-section public-section-soft" id="how-it-works">
      <div className="section-intro center">
        <p className="eyebrow">HOW IT WORKS</p>
        <h2>Five steps to lifelong peace of mind.</h2>
      </div>
      <div className="steps-flow">
        {howSteps.map((step, i) => (
          <React.Fragment key={step.num}>
            <article className="step-card">
              <span className="step-num">{step.num}</span>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </article>
            {i < howSteps.length - 1 && <div className="step-connector" aria-hidden="true" />}
          </React.Fragment>
        ))}
      </div>
      <div className="center-action">
        <Link to="/how-it-works" className="text-link">
          Full verification process <LuArrowRight aria-hidden="true" />
        </Link>
      </div>
    </section>

    {/* Comparison */}
    <section className="public-section" id="why-us">
      <div className="section-intro center">
        <p className="eyebrow">WHY KEEPMYASSET</p>
        <h2>Built for families—not generic cloud storage.</h2>
      </div>
      <div className="comparison-table">
        <div className="comparison-header">
          <span>Others</span>
          <span>KeepMyAsset</span>
        </div>
        {comparisonRows.map(([others, us]) => (
          <div className="comparison-row" key={others}>
            <span className="comparison-others">{others}</span>
            <span className="comparison-us">
              <LuCheck aria-hidden="true" /> {us}
            </span>
          </div>
        ))}
      </div>
    </section>

    {/* Emotional */}
    <section className="public-section emotional-section">
      <div className="emotional-content">
        <p className="eyebrow light">PEACE OF MIND</p>
        <h2>People don&apos;t buy software. They buy peace of mind.</h2>
        <p>
          Your family deserves to know about the insurance you paid for, the investments you built,
          and the documents that protect their future—not a database they&apos;ll never find.
        </p>
        <div className="emotional-tags">
          {['Father', 'Mother', 'Son', 'Daughter', 'Family', 'Insurance', 'Legacy', 'Security', 'Hope'].map(
            (tag) => (
              <span key={tag}>{tag}</span>
            )
          )}
        </div>
        <button type="button" className="button button-accent" onClick={startAccount}>
          Start Organizing Assets
        </button>
      </div>
    </section>

    {/* Security teaser */}
    <section className="public-section security-panel">
      <div>
        <p className="eyebrow">SECURITY</p>
        <h2>Your strongest page should be our security promise.</h2>
        <p>
          Encryption, access control, Google login, backups, data isolation, and no employee browsing
          of your files.
        </p>
        <div className="inline-actions">
          <Link to="/security" className="button button-light">
            Read our security approach
          </Link>
        </div>
      </div>
      <div className="security-icons">
        <div><LuLock /> AES-256 Encryption</div>
        <div><LuUserCheck /> Access Control</div>
        <div><LuCloud /> Google Cloud</div>
        <div><LuShield /> No Employee Access</div>
      </div>
    </section>

    {/* FAQ */}
    <section className="public-section public-section-soft" id="faq">
      <div className="section-intro center">
        <p className="eyebrow">FAQ</p>
        <h2>Questions families ask before they trust us.</h2>
      </div>
      <div className="faq-list">
        {faqs.map(({ q, a }) => (
          <details className="faq-item" key={q}>
            <summary>{q}</summary>
            <p>{a}</p>
          </details>
        ))}
      </div>
      <div className="center-action">
        <Link to="/faq" className="text-link">
          View all FAQs <LuArrowRight aria-hidden="true" />
        </Link>
      </div>
    </section>

    {/* Final CTA */}
    <section className="public-cta">
      <div>
        <p className="eyebrow">GET STARTED</p>
        <h2>One secure place for every asset your nominees should know about.</h2>
        <p>Free to start. Add your first asset in under 2 minutes.</p>
      </div>
      <button type="button" className="button button-accent" onClick={startAccount}>
        Start Organizing Assets <LuArrowRight aria-hidden="true" />
      </button>
    </section>

    {/* Mobile sticky CTA */}
    <div className="mobile-sticky-cta">
      <button type="button" className="button button-accent" onClick={startAccount}>
        Start Organizing Assets
      </button>
    </div>
  </div>
  </>
  );
};

export const PublicPage = ({ path }) => {
  switch (path) {
    case '/how-it-works':
      return <HowItWorksPage />;
    case '/security':
      return <SecurityPage />;
    case '/faq':
      return <FaqPage />;
    case '/about':
      return <AboutPage />;
    default:
      return <HowItWorksPage />;
  }
};
